import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, MessageSquare, ShieldCheck, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

const CATEGORIES = [
  { value: "general", label: "General feedback" },
  { value: "bug", label: "Report a bug" },
  { value: "idea", label: "Feature idea" },
  { value: "question", label: "Question" },
];

const HELP_LINKS = [
  { icon: BookOpen, title: "Documentation", desc: "Guides and articles", to: "/resources" },
  { icon: ShieldCheck, title: "Security & privacy", desc: "How we protect your data", to: "/security" },
  { icon: Mail, title: "Contact us", desc: "Reach the team directly", to: "/contact" },
];

const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary";

export default function HelpFeedback() {
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/feedback", { category, message });
      track("feedback_submitted", { category });
      toast.success(data.message || "Thanks for the feedback!");
      setMessage("");
      setCategory("general");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send feedback.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="help-feedback">
      <Seo title="Help & Feedback" description="Get help and share feedback about StUpEco Workspace." path="/app/help" />
      <div className="mb-8">
        <p className="eyebrow text-brand-teal">Help & Feedback</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Help & Feedback</h1>
        <p className="mt-2 text-sm text-muted-foreground">Find answers or tell us what would make Workspace better.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          {HELP_LINKS.map((h) => (
            <Link key={h.title} to={h.to} className="card-hover flex items-center gap-4 rounded-2xl border border-border bg-card p-5 hover:border-foreground/20" data-testid={`help-link-${h.title.toLowerCase().split(" ")[0]}`}>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                <h.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-base font-semibold tracking-tight">{h.title}</p>
                <p className="text-sm text-muted-foreground">{h.desc}</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-brand-teal" />
            <h2 className="font-display text-lg font-semibold tracking-tight">Share feedback</h2>
          </div>
          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="feedback-form">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="feedback-category-select">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Message</label>
              <textarea required rows={5} value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputCls} resize-none`} data-testid="feedback-message-input" placeholder="Tell us what's on your mind…" />
            </div>
            <button type="submit" disabled={sending} data-testid="feedback-submit-btn" className="rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60">
              {sending ? "Sending…" : "Send feedback"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
